"use client";

import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

const DIFFERENTIATORS = [
  {
    title: "Multi-dimensional",
    description:
      "Integrates a range of interventions, including spiritual growth, strategic planning, and empowerment with knowledge and skills, to address the complexities of addiction and related dysfunction.",
    src: "/images/about/methodology/About_Multi-Dimensional.avif",
    alt: "A woman with glasses reads a workbook on a couch.",
  },
  {
    title: "Evidence-based",
    description:
      "Since founding, we\u2019ve witnessed thousands of lives touched and transformed through recovery services empowered by Project Exodus.",
    src: "/images/about/methodology/About_Evidence-based.avif",
    alt: "A man with glasses reads a book during a session.",
  },
  {
    title: "Restorative",
    description:
      "We believe in a life-affirming approach based on biblical principles of redemption and innate worth.",
    src: "/images/about/methodology/About_Restorative.avif",
    alt: "Three people share a heartfelt group hug.",
  },
  {
    title: "Integrative",
    description:
      "Tools can be used to build fully integrated recovery programmes that retain your unique identity and culture.",
    src: "/images/about/methodology/About_ScripturallyAligned.avif",
    alt: "A group session takes place inside a beautiful church.",
  },
  {
    title: "Sustainable",
    description:
      "Core content encourages extended participation in recovery-related activities, understanding that sustainable transformation is a process that takes time.",
    src: "/images/about/methodology/About_Sustainable.avif",
    alt: "Two women sit together, showing the text on their shirts.",
  },
  {
    title: "Inclusive",
    description:
      "Strategies provides centralised recovery strategies for all types of addictive disorders, as well as for loved ones negatively impacted by addiction.",
    src: "/images/about/methodology/About_Inclusive.avif",
    alt: "A large, diverse group poses together for a photo.",
  },
] as const;

const SLIDE_DURATION = 6000;

const slideVariants = {
  enter: { opacity: 0, x: 32 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -32 },
};

export function WhatMakesUsDifferent() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const advance = useCallback(() => {
    setIndex((i) => (i + 1) % DIFFERENTIATORS.length);
  }, []);

  useEffect(() => {
    if (paused) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      return;
    }
    timerRef.current = setInterval(advance, SLIDE_DURATION);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [paused, advance]);

  // index is always in range [0, DIFFERENTIATORS.length) — state initialises at 0 and advances via modulo
  // biome-ignore lint/style/noNonNullAssertion: index is bounded by DIFFERENTIATORS.length
  const current = DIFFERENTIATORS[index]!;

  return (
    <section className="py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 md:items-start">
          {/* Left — fixed heading + description + feature tabs */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h1 className="heading-1 font-bold">
                What makes us{" "}
                <span className="font-serif italic">different</span>
              </h1>
              <p className="body-lg text-muted-foreground">
                At Project Exodus, we believe in a world where recovery is
                accessible to all. We aim to radically revolutionise the way
                recovery works.
              </p>
            </div>

            {/* Clickable feature pills */}
            <div className="flex flex-wrap gap-2">
              {DIFFERENTIATORS.map((item, i) => (
                <button
                  className={`rounded-full px-4 py-1.5 font-medium text-sm transition-colors ${
                    i === index
                      ? "bg-foreground text-background"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  key={item.title}
                  onClick={() => {
                    setIndex(i);
                    setPaused(true);
                  }}
                  type="button"
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>

          {/* Right — animated feature slide */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: hover to pause auto-advance — visual effect, no interactive role needed */}
          {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: hover to pause auto-advance — visual effect, no interactive role needed */}
          <div
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                animate="center"
                className="space-y-4"
                exit="exit"
                initial="enter"
                key={index}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                variants={slideVariants}
              >
                <div className="relative aspect-square overflow-hidden rounded-2xl">
                  <Image
                    alt={current.alt}
                    className="object-cover"
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    src={current.src}
                  />
                </div>
                <h3 className="heading-4 font-semibold">{current.title}</h3>
                <p className="body-base text-muted-foreground leading-relaxed">
                  {current.description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Progress dots */}
            <div className="mt-6 flex gap-2">
              {DIFFERENTIATORS.map((item, i) => (
                <button
                  aria-label={`View ${item.title}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === index
                      ? "w-6 bg-foreground"
                      : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  }`}
                  key={item.title}
                  onClick={() => {
                    setIndex(i);
                    setPaused(true);
                  }}
                  type="button"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
